import type { LiveAttack } from "../../types/generated/live";
import { Box } from "../ui/box";
import { Icon, type IconName } from "../ui/icon";
import { RollChip, type RollSpec } from "../ui/roll_chip";
import { Term } from "../ui/term";
import { foldOf } from "./folds";
import { knockoutText, propertiesText, showsBac, signed } from "./format";
import { termInfo } from "./glossary";
import { interpolate, type Labels } from "./labels";
import { attackRoll, plainRoll, testRoll, withFlat } from "./rolls";
import type { SheetView } from "./sheet_view";
import { TABLE_CELL, TABLE_HEAD } from "./styles";

function damageText(attack: LiveAttack): { main: string; twoHanded: string | null } {
	const damage = attack.damage;
	if (!damage) return { main: "-", twoHanded: null };
	const bonus = damage.bonus.total === 0 ? "" : signed(damage.bonus.total);
	return { main: `${damage.dice}${bonus}`, twoHanded: damage.two_handed_dice ? `${damage.two_handed_dice}${bonus}` : null };
}

// The rolls of an attack: what its damage cell shows (and the two-handed dice), by the same numbers.
function damageRolls(attack: LiveAttack, name: string, labels: Labels): { main: RollSpec | null; twoHanded: RollSpec | null } {
	const damage = attack.damage;
	if (!damage) return { main: null, twoHanded: null };
	const flat = damage.bonus.total;
	return {
		main: plainRoll(withFlat(damage.dice, flat), interpolate(labels.descriptions.damage, { weapon: name })),
		twoHanded: damage.two_handed_dice
			? plainRoll(withFlat(damage.two_handed_dice, flat), interpolate(labels.descriptions.two_handed_damage, { weapon: name }))
			: null,
	};
}

const KIND_ICON: Record<LiveAttack["kind"], IconName> = {
	melee: "melee",
	shoot: "ranged",
	thrown: "throwing",
	ammo: "ammunition",
	natural: "naturalWeapon",
	unarmed: "unarmed",
};

const TYPE_ICON = { bludgeoning: "bludgeoning", piercing: "piercing", slashing: "slashing" } as const;

function Titled({ name, title }: { name: IconName; title: string }) {
	return (
		<span title={title} role="img" aria-label={title} class="inline-flex shrink-0 text-ink-muted">
			<Icon name={name} class="h-4 w-4" />
		</span>
	);
}

// What an item says of itself; the unarmed attack's is the chance to knock out, rolled on 1d6. `cut` is the wide table's
// column, which holds one line and keeps the rest in its title; under a name the text wraps whole.
function Properties({ attack, labels, cut = false }: { attack: LiveAttack; labels: Labels; cut?: boolean }) {
	if (attack.kind === "unarmed") {
		const range = attack.knockout_range ?? 1;
		return (
			<span class="inline-flex flex-wrap items-center gap-1.5">
				{labels.attacks.knockout_chance}
				<RollChip size="sm" spec={testRoll(labels.attacks.knockout, range, "1d6")}>
					{knockoutText(range, labels.attacks)}
				</RollChip>
			</span>
		);
	}
	const text = propertiesText(attack, labels.attacks);
	if (!cut) return <>{text}</>;
	return (
		<span class="block max-w-[8rem] truncate" title={text}>
			{text}
		</span>
	);
}

export function Attacks({ view, labels }: { view: SheetView; labels: Labels }) {
	const { terms, attacks } = labels;
	return (
		<Box icon="melee" title={labels.boxes.attacks} fold={foldOf(view, "attacks", labels.boxes.attacks, labels)} class="px-3 pb-2">
			<table class="cs-attacks w-full border-collapse text-[14.5px]">
				<thead>
					<tr>
						<th class={`${TABLE_HEAD} w-px`}>
							<Term label="×" {...termInfo(labels, "attacks_count")} />
						</th>
						<th class={`${TABLE_HEAD} cs-attack-name`}>{attacks.weapon}</th>
						<th class={`${TABLE_HEAD} cs-narrow-hidden`}>
							<Term label={attacks.range} {...termInfo(labels, "range")} />
						</th>
						<th class={TABLE_HEAD}>
							<Term label={terms.attack_bonus_melee.abbr} {...termInfo(labels, "bac")} />
						</th>
						<th class={TABLE_HEAD}>
							<Term label={terms.attack_bonus_ranged.abbr} {...termInfo(labels, "bad")} />
						</th>
						<th class={TABLE_HEAD}>{attacks.damage}</th>
						<th class={`${TABLE_HEAD} cs-narrow-hidden`}>{attacks.properties}</th>
					</tr>
				</thead>
				<tbody>
					{view.derived.attacks.map((attack, index) => {
						const damage = damageText(attack);
						const name = attack.name ?? attacks.unarmed;
						// A weapon thrown and shot is two rows; the feed tells them apart by the mode in the roll's name.
						const paired = attack.item_id !== null && view.derived.attacks.some((other) => other !== attack && other.item_id === attack.item_id);
						const rollName = paired ? `${name} (${attacks[attack.kind]})` : name;
						const rolls = damageRolls(attack, rollName, labels);
						return (
							<tr key={attack.item_id ? `${attack.item_id}-${attack.kind}` : `unarmed-${index}`}>
								<td class={`${TABLE_CELL} whitespace-nowrap`}>{attack.number_of_attacks}×</td>
								<td class={`${TABLE_CELL} cs-attack-name cs-break`}>
									<span class="inline-flex items-center gap-1.5">
										<Titled name={KIND_ICON[attack.kind]} title={attacks[attack.kind]} />
										<b>{name}</b>
									</span>
									<div class="cs-under-name mt-0.5 text-xs text-ink-muted">
										<Properties attack={attack} labels={labels} />
									</div>
								</td>
								<td class={`${TABLE_CELL} cs-narrow-hidden`}>{attack.range ? `${attack.range} m` : "-"}</td>
								<td class={TABLE_CELL}>
									{showsBac(attack) && (
										<RollChip size="sm" spec={attackRoll(attack.bac.total, interpolate(labels.descriptions.attack, { weapon: rollName }))}>
											{signed(attack.bac.total)}
										</RollChip>
									)}
								</td>
								<td class={TABLE_CELL}>
									{attack.bad && (
										<RollChip size="sm" spec={attackRoll(attack.bad.total, interpolate(labels.descriptions.ranged_attack, { weapon: rollName }))}>
											{signed(attack.bad.total)}
										</RollChip>
									)}
								</td>
								<td class={TABLE_CELL}>
									<div class="flex flex-wrap items-center gap-2">
										{attack.kind === "unarmed" ? (
											<Term label={attacks.knockout} {...termInfo(labels, "knockout", attacks.knockout)} />
										) : (
											<RollChip size="sm" spec={rolls.main}>
												{damage.main}
											</RollChip>
										)}
										{damage.twoHanded && (
											<RollChip size="sm" spec={rolls.twoHanded}>
												<Titled name="twoHanded" title={attacks.two_handed} />
												{damage.twoHanded}
											</RollChip>
										)}
										{attack.damage_type && <Titled name={TYPE_ICON[attack.damage_type]} title={attacks[attack.damage_type]} />}
									</div>
								</td>
								<td class={`${TABLE_CELL} cs-narrow-hidden text-xs text-ink-muted`}>
									<Properties attack={attack} labels={labels} cut />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			{view.derived.equip_conflicts.map((conflict) => (
				<p key={conflict} class="mt-2 text-xs text-ink-muted">
					{labels.equipment_conflicts[conflict]}
				</p>
			))}
		</Box>
	);
}
